import { useState, useCallback, useEffect } from "react";
import { StripeCheckout }  from "../../api/stripeCheckout.js";
import { UpdateSubscription } from "../../api/UpdateSubscription.js";
import RecurringModal from './RecurringModal';
import { commonErrorToastMessage } from "../../components/toastify/AllToastifyErrors";

const PricingModal = () => {
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [isAnnual, setIsAnnual] = useState(true);
  const [activePlan, setActivePlan] = useState(null);
  const [isPlanActive, setIsPlanActive] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [pendingPlanType, setPendingPlanType] = useState(null);
  const [isUpgrading, setIsUpgrading] = useState(false);
  
// Check if plan is expired based on multiple data sources
  const checkPlanExpiry = useCallback(() => {
    // First check explicit expiry flag
    const isPlanExpiredFlag = localStorage.getItem("isPlanExpired");
    if (isPlanExpiredFlag === "true") {
      return false;
    }

    // Check payment status - if explicitly set to active, trust it
    const paymentStatus = localStorage.getItem("paymentStatus") || 
                         localStorage.getItem("PaymentStatus") ||
                         localStorage.getItem("payment_status");
    
    if (paymentStatus) {
      const statusLower = paymentStatus.toLowerCase();
      if (statusLower === "active") {
        return true;
      } else {
        return false;
      }
    }

    // If no payment status, check expiry date
    const expiryDateStr = localStorage.getItem("planExpiryDate") ||
                          localStorage.getItem("subscriptionValidity") ||
                          localStorage.getItem("monthEndDate") ||
                          localStorage.getItem("Subscription Validity");
    
    if (!expiryDateStr) {
      // If a tier type exists but no expiry info, treat as active
      // This will be checked in updateActivePlan
      return null; // Return null to indicate "unknown" state
    }

    try {
      const expiryDate = new Date(expiryDateStr);
      
      if (isNaN(expiryDate.getTime())) {
        console.log("Invalid expiry date format - treating as active if tier exists");
        return null;
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expiryDate.setHours(23, 59, 59, 999);
      
      const isActive = expiryDate >= today;
      
      return isActive;
    } catch (error) {
      console.log("Error parsing expiry date:", error);
      return null;
    }
  }, []);

  const Version = localStorage.getItem('Version');
  
  const updateActivePlan = useCallback(() => {
    let tierType = localStorage.getItem("TIERTYPE") ||
                   localStorage.getItem("tierType") ||
                   localStorage.getItem("subscriptionType") ||
                   localStorage.getItem("Subscription Type");
    
    if (tierType === "null" || tierType === "undefined" || tierType === "") {
      tierType = null;
    }
    
    const isActive = checkPlanExpiry();
    
    setActivePlan(tierType);
    setIsPlanActive(isActive);
  }, [checkPlanExpiry]);

  useEffect(() => {
    updateActivePlan();

    const handleStorageChange = (e) => {
      const relevantKeys = [
        "TIERTYPE", "tierType", "subscriptionType",
        "planExpiryDate", "subscriptionValidity", "monthEndDate",
        "isPlanExpired", "paymentStatus", "PaymentStatus"
      ];
      
      if (relevantKeys.includes(e.key)) {
        updateActivePlan();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    
    const handleFocus = () => {
      updateActivePlan();
    };
    
    window.addEventListener("focus", handleFocus);

    const intervalId = setInterval(() => {
      updateActivePlan();
    }, 30000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleFocus);
      clearInterval(intervalId);
    };
  }, [updateActivePlan]);

    // NEW: Check if trying to switch billing cycles (monthly <-> annually)
  const isBillingCycleSwitch = useCallback((targetPlanType) => {
    if (!isPlanActive || !activePlan) return false;
    
    const currentIsMonthly = activePlan.includes('monthly');
    const targetIsMonthly = targetPlanType.includes('monthly');
    
    // Return true if trying to switch between monthly and annual
    return currentIsMonthly !== targetIsMonthly;
  }, [activePlan, isPlanActive]);

  // NEW: Check if the target plan is an upgrade/downgrade
  const isUpgradeOrDowngrade = useCallback((targetPlanType) => {
    if (!isPlanActive || !activePlan) return false;
    
    // Get current plan billing cycle
    const currentIsMonthly = activePlan.includes('monthly');
    const targetIsMonthly = targetPlanType.includes('monthly');
    
    // Can only upgrade/downgrade within same billing cycle
    if (currentIsMonthly !== targetIsMonthly) {
      return false;
    }
    
    // Check if it's a different plan
    return activePlan !== targetPlanType;
  }, [activePlan, isPlanActive]);

  // NEW: Process plan upgrade/downgrade
  const processUpgradeDowngrade = useCallback(async (newPlanType) => {
    try {
      setIsUpgrading(true);
      setLoadingPlan(newPlanType);

      const userid = localStorage.getItem("Id") ||
                     localStorage.getItem("Userid") ||
                     localStorage.getItem("userid") ||
                     localStorage.getItem("userId");
      
      const Email = localStorage.getItem("email") ||
                    localStorage.getItem("Email") ||
                    localStorage.getItem("userEmail");

      if (!userid || !Email) {
        alert("Missing user information. Please login before changing plans.");
        setLoadingPlan(null);
        setIsUpgrading(false);
        return;
      }

      const response = await UpdateSubscription(userid, Email, newPlanType);

      if (response?.data?.success) {

        // If a checkoutUrl is provided, redirect to it
        if (response.data.checkoutUrl) {
          window.location.href = response.data.checkoutUrl;
          return;
        }
      } else {
        throw new Error(response?.data?.error || "Failed to update subscription");
      }
    } catch (err) {
      console.error("Error updating subscription:", err);
      
      if (err.response?.data?.requiresNewSubscription) {
        alert("Cannot switch between monthly and annual plans. Please cancel your current subscription and create a new one.");
      } else {
        alert(`Failed to update plan: ${err.response?.data?.error || err.message || "Unknown error"}`);
      }
    } finally {
      setLoadingPlan(null);
      setIsUpgrading(false);
    }
  }, [activePlan, updateActivePlan]);

  // Handle the actual payment processing (for new subscriptions)
  const processPlanPurchase = useCallback(async (planType) => {
    try {
      
      setLoadingPlan(planType);

      const userid = localStorage.getItem("Id") ||
                     localStorage.getItem("Userid") ||
                     localStorage.getItem("userid") ||
                     localStorage.getItem("userId");
      
      const Email = localStorage.getItem("email") ||
                    localStorage.getItem("Email") ||
                    localStorage.getItem("userEmail");

      if (!userid || !Email) {
        alert("Missing user information. Please login before purchasing.");
        setLoadingPlan(null);
        return;
      }

      const resp = await StripeCheckout(userid, Email, planType);
      const url = resp?.data?.url || resp?.url || (resp && typeof resp === "string" ? resp : null);

      if (!url) {
        console.error("Stripe checkout did not return a url:", resp);
        alert("Unable to start checkout. Try again later.");
        setLoadingPlan(null);
        return;
      }

      window.location.href = url;

    } catch (err) {
      console.error("Error starting Stripe checkout:", err);
      alert(`Checkout failed: ${err.message || "Unknown error"}`);
    } finally {
      setLoadingPlan(null);
    }
  }, []);

  // Handle initial button click - show popup first (or upgrade directly)
// Handle initial button click - show popup first (or upgrade directly)
  const handleBuyPlan = useCallback((planType) => {
    
    // Check if trying to switch billing cycles (monthly ↔ annually)
    if (isBillingCycleSwitch(planType)) {
      const currentBillingType = activePlan.includes('monthly') ? 'Monthly' : 'Annual';
      const targetBillingType = planType.includes('monthly') ? 'Monthly' : 'Annual';
      
      commonErrorToastMessage(
        `⚠️ Billing Cycle Change Not Allowed\n\n` +
        `You currently have an active ${currentBillingType} subscription. ` +
        `To switch to a ${targetBillingType} plan, please cancel your current subscription first, ` +
        `then purchase the new ${targetBillingType} plan.\n\n`
      );
      return; // Stops execution here - prevents any further action
    }
    
    // Check if this is an upgrade/downgrade
    if (isUpgradeOrDowngrade(planType)) {
      // Show confirmation for plan changes
      const currentTier = activePlan.includes('tier1') ? 'Tier 1' : 'Tier 2';
      const newTier = planType.includes('tier1') ? 'Tier 1' : 'Tier 2';
      
      if (window.confirm(`You are about to change from ${currentTier} to ${newTier}. Your billing will be prorated accordingly. Continue?`)) {
        processUpgradeDowngrade(planType);
      }
    } else {
      // New subscription - show recurring payment popup
      setPendingPlanType(planType);
      setShowPopup(true);
    }
  }, [isBillingCycleSwitch, isUpgradeOrDowngrade, activePlan, processUpgradeDowngrade]);

  // Handle user continuing after seeing the popup
  const handleContinue = useCallback(() => {
    setShowPopup(false);
    if (pendingPlanType) {
      processPlanPurchase(pendingPlanType);
      setPendingPlanType(null);
    }
  }, [pendingPlanType, processPlanPurchase]);

  // Handle user closing the popup
  const handleClosePopup = useCallback(() => {
    setShowPopup(false);
    setPendingPlanType(null);
  }, []);

  // Determine if current plan button should be disabled
  const isCurrentPlan = (planType) => {
    return isPlanActive && activePlan === planType;
  };

  // NEW: Get button text based on plan state
  const getButtonText = useCallback((planType) => {
    if (isCurrentPlan(planType)) {
      return 'Current Plan';
    }
    
    if (loadingPlan === planType) {
      return isUpgrading ? "Updating Plan..." : "Processing...";
    }
    
    if (isUpgradeOrDowngrade(planType)) {
      const currentTier = activePlan?.includes('tier1') ? 1 : 2;
      const targetTier = planType.includes('tier1') ? 1 : 2;
      
      if (targetTier > currentTier) {
        return `Upgrade to ${isAnnual ? 'Annual' : 'Monthly'}`;
      } else {
        return `Switch to ${isAnnual ? 'Annual' : 'Monthly'}`;
      }
    }
    
    return `Choose ${isAnnual ? 'Annual' : 'Monthly'} Plan`;
  }, [isCurrentPlan, loadingPlan, isUpgrading, isUpgradeOrDowngrade, activePlan, isAnnual]);

  // Show expiry warning if plan exists but is expired
  const showExpiryWarning = activePlan && !isPlanActive;

  return (
    <div className="w-full bg-gradient-to-br from-blue-50 to-purple-50 py-6 px-4">
      <div className="max-w-[1280px] mx-auto">
        {/* Header Section */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Plan</h1>
          <p className="text-sm text-gray-600">Select the perfect plan for your needs</p>
          
          {/* Expiry Warning */}
          {Version === 'Free' ? (
            ""
          ):(
            <>
              {showExpiryWarning && (
                <div className="mt-4 mx-auto max-w-2xl bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
                  <div className="flex items-center gap-3 text-orange-800">
                    <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="text-left">
                      <p className="font-bold text-sm">Your subscription has expired</p>
                      <p className="text-xs mt-1">Renew your plan below to continue enjoying premium features</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Loader Overlay */}
        {loadingPlan && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg px-8 py-5 flex items-center gap-4 shadow-xl">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-purple-600"></div>
              <div className="text-base text-gray-800 font-medium">
                {isUpgrading ? 'Updating your subscription...' : 'Redirecting to secure checkout...'}
              </div>
            </div>
          </div>
        )}

        {/* Toggle Switch */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3 bg-white rounded-full p-1.5 shadow-md border border-gray-200">
            <span className={`text-sm font-semibold px-3 py-1 transition-colors ${!isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative w-12 h-6 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
                isAnnual ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              aria-label="Toggle billing period"
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                  isAnnual ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
            <span className={`text-sm font-semibold transition-colors ${isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
              Annually
              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">
                Save 15%
              </span>
            </span>
          </div>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {/* Tier 1 Card */}
          <div
            className={`flex flex-col bg-white rounded-xl shadow-lg border-2 p-6 relative
            transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${
              isCurrentPlan(isAnnual ? 'tier1_annually' : 'tier1_monthly')
                ? 'border-green-500 ring-2 ring-green-200'
                : 'border-gray-200'
            }`}
          >
            {/* Active Badge */}
            {isCurrentPlan(isAnnual ? 'tier1_annually' : 'tier1_monthly') && (
              <span className="absolute -top-3 right-4 bg-green-500 text-white text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                ACTIVE
              </span>
            )}
            
            <h2 className="text-lg font-bold text-gray-800 uppercase tracking-wide text-center mb-4">
              TIER ONE
            </h2>

            {!isAnnual ? (
              <div className="flex flex-col items-center bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-xs font-semibold text-blue-700 mb-2">
                  Billed Monthly
                </p>
                <p className="text-4xl font-bold text-gray-900">£19.99</p>
                <p className="text-gray-600 text-xs mt-1">per month</p>
              </div>
            ) : (
              <div className="flex flex-col items-center bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-xs font-semibold text-blue-700 mb-2">
                  15% OFF • Billed Annually
                </p>
                <p className="text-4xl font-bold text-gray-900">£204.00</p>
                <p className="text-gray-600 text-xs mt-1">for 12 months (£17.00/mo avg)</p>
              </div>
            )}

            <button 
              onClick={() => handleBuyPlan(isAnnual ? "tier1_annually" : "tier1_monthly")}
              disabled={loadingPlan !== null || isCurrentPlan(isAnnual ? 'tier1_annually' : 'tier1_monthly')}
              className={`w-full text-white text-sm font-semibold px-4 py-2.5 rounded-lg shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-4 ${
                isCurrentPlan(isAnnual ? 'tier1_annually' : 'tier1_monthly')
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg'
              }`}
            >
              {getButtonText(isAnnual ? "tier1_annually" : "tier1_monthly")}
            </button>

            <div className="pt-4 border-t border-gray-200">
              <p className="font-bold text-gray-800 text-sm text-center mb-3">Plan Details:</p>
              <div className="space-y-2 text-xs text-gray-700">
                <p className="flex items-start">
                  <span className="mr-2 mt-0.5">•</span>
                  <span>Single User License</span>
                </p>
                <p className="flex items-start">
                  <span className="mr-2 mt-0.5">•</span>
                  <span>Devise up to 5 quiz per month</span>
                </p>
                <p className="flex items-start">
                  <span className="mr-2 mt-0.5">•</span>
                  <span>Maximum 10 questions per quiz</span>
                </p>
                {isAnnual && (
                  <p className="font-bold text-green-600 pt-2 text-center text-xs">
                    💰 Save £35.88 annually!
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Tier 2 Card */}
          <div
            className={`flex flex-col bg-white rounded-xl shadow-lg border-2 p-6 relative
            transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${
              isCurrentPlan(isAnnual ? 'tier2_annually' : 'tier2_monthly')
                ? 'border-green-500 ring-2 ring-green-200'
                : 'border-purple-300'
            }`}
          >
            {isCurrentPlan(isAnnual ? 'tier2_annually' : 'tier2_monthly') ? (
              <span className="absolute -top-3 right-4 bg-green-500 text-white text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                ACTIVE
              </span>
            ) : (
              <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-md">
                ⭐ BEST VALUE
              </span>
            )}
            
            <h2 className="text-lg font-bold text-gray-800 uppercase tracking-wide text-center mb-4">
              TIER TWO
            </h2>

            {!isAnnual ? (
              <div className="flex flex-col items-center bg-purple-50 border-2 border-purple-200 rounded-lg p-4 mb-4">
                <p className="text-xs font-semibold text-purple-700 mb-2">
                  Billed Monthly
                </p>
                <p className="text-4xl font-bold text-gray-900">£39.99</p>
                <p className="text-gray-600 text-xs mt-1">per month</p>
              </div>
            ) : (
              <div className="flex flex-col items-center bg-purple-50 border-2 border-purple-200 rounded-lg p-4 mb-4">
                <p className="text-xs font-semibold text-purple-700 mb-2">
                  15% OFF • Billed Annually
                </p>
                <p className="text-4xl font-bold text-gray-900">£409.00</p>
                <p className="text-gray-600 text-xs mt-1">for 12 months (£34.08/mo avg)</p>
              </div>
            )}

            <button 
              onClick={() => handleBuyPlan(isAnnual ? "tier2_annually" : "tier2_monthly")}
              disabled={loadingPlan !== null || isCurrentPlan(isAnnual ? 'tier2_annually' : 'tier2_monthly')}
              className={`w-full text-white text-sm font-semibold px-4 py-2.5 rounded-lg shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-4 ${
                isCurrentPlan(isAnnual ? 'tier2_annually' : 'tier2_monthly')
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-purple-600 hover:bg-purple-700 hover:shadow-lg'
              }`}
            >
              {getButtonText(isAnnual ? "tier2_annually" : "tier2_monthly")}
            </button>

            <div className="pt-4 border-t border-gray-200">
              <p className="font-bold text-gray-800 text-sm text-center mb-3">Plan Details:</p>
              <div className="space-y-2 text-xs text-gray-700">
                <p className="flex items-start">
                  <span className="mr-2 mt-0.5">•</span>
                  <span>3 User Licenses</span>
                </p>
                <p className="flex items-start">
                  <span className="mr-2 mt-0.5">•</span>
                  <span>Devise up to 5 quiz per month per user</span>
                </p>
                <p className="flex items-start">
                  <span className="mr-2 mt-0.5">•</span>
                  <span>Maximum of 30 questions per quiz</span>
                </p>
                {isAnnual && (
                  <p className="font-bold text-green-600 pt-2 text-center text-xs">
                    💰 Save £70.88 annually!
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Tier 3 - Enterprise Card */}
          <div className="flex flex-col bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6 relative transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl md:col-span-2 lg:col-span-1">
            <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-md">
              🚀 ENTERPRISE
            </span>
            
            <h2 className="text-lg font-bold text-gray-800 uppercase tracking-wide text-center mb-4">
              TIER THREE
            </h2>

            <div className="flex flex-col items-center bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-4 mb-4">
              <p className="text-xs font-bold text-purple-700 mb-2 uppercase tracking-wide">
                Bespoke Pricing
              </p>
              <p className="text-xs text-gray-700 text-center leading-relaxed mb-3">
                Contact us for custom enterprise solutions tailored to your needs
              </p>
              <a 
                href="mailto:jonathan@eduvate.biz" 
                className="text-blue-600 font-semibold hover:text-blue-700 hover:underline text-xs transition-colors"
              >
                📧 jonathan@eduvate.biz
              </a>
            </div>

            <button 
              onClick={() => window.location.href = 'mailto:jonathan@eduvate.biz'}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg shadow-md transition-all duration-200 hover:shadow-lg mb-4"
            >
              Contact Sales Team
            </button>

            <div className="pt-4 border-t border-gray-200">
              <p className="font-bold text-gray-800 text-sm text-center mb-3">Premium Features Include:</p>
              <div className="space-y-2 text-xs text-gray-700">
                <p className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Unlimited quiz per month</span>
                </p>
                <p className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>White labelling</span>
                </p>
                <p className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Priority support</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Recurring Modal */}
      <RecurringModal
        isOpen={showPopup}
        onContinue={handleContinue}
        onClose={handleClosePopup}
      />
    </div>
  );
};
export default PricingModal;